import apiClient from '../client'
import type {
  ExpedienteSearchItemResponse,
  ExpedienteDetailResponse,
} from '../models/response/expediente'
import type {
  ExpedienteIndiceResponse,
  IndiceVerificacionResponse,
  IndiceVersionResponse,
} from '../models/response/indiceDigital'

const BASE_PATH = '/expedientes'

export interface ExpedienteSearchOptions {
  /** Restrict to a single ExpedienteType. 0 = Miner, 1 = Batch. */
  type?: number
}

export const expedienteService = {
  async search(q: string, options?: ExpedienteSearchOptions): Promise<ExpedienteSearchItemResponse[]> {
    const params = new URLSearchParams()
    if (q) params.set('q', q)
    if (options?.type !== undefined) params.set('type', String(options.type))
    const query = params.toString()
    return apiClient.get<ExpedienteSearchItemResponse[]>(
      `${BASE_PATH}/search${query ? `?${query}` : ''}`,
    )
  },

  /** Paginado por grupos de nivel superior: cada página trae las raíces de la
   *  página con todos sus descendientes. Usar `hasMore` para cargar la siguiente. */
  async getByCode(code: string, page = 1, pageSize = 20): Promise<ExpedienteDetailResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return apiClient.get<ExpedienteDetailResponse>(`${BASE_PATH}/${encodeURIComponent(code)}?${params.toString()}`)
  },

  /** Returns a URL the browser can GET directly to receive the streamed ZIP as an attachment. */
  getDownloadZipUrl(code: string): string {
    return apiClient.buildUrl(`${BASE_PATH}/${encodeURIComponent(code)}/download`)
  },

  /** URL for the per-item primary file. Auth header is injected by the fetch-wrapped download helper. */
  getItemFileUrl(code: string, itemId: string): string {
    return apiClient.buildUrl(`${BASE_PATH}/${encodeURIComponent(code)}/item/${itemId}/file`)
  },

  // ── Índice digital (D.S. 029-2021-PCM arts. 39-44) ───────────────────────
  //
  // Todo son lecturas con `expedientes.view` salvo `sealIndice`, que además exige
  // `expedientes.indice.emit`: la línea está en escribir, no en la sensibilidad
  // del dato.

  /** Índice EN VIVO: lo que se sellaría si se sellara ahora. No persiste nada. */
  async getIndice(code: string): Promise<ExpedienteIndiceResponse> {
    return apiClient.get<ExpedienteIndiceResponse>(`${BASE_PATH}/${encodeURIComponent(code)}/indice`)
  },

  /** Versiones selladas, de la más reciente a la más antigua. NO las verifica. */
  async getIndiceVersiones(code: string): Promise<IndiceVersionResponse[]> {
    return apiClient.get<IndiceVersionResponse[]>(`${BASE_PATH}/${encodeURIComponent(code)}/indice/versiones`)
  },

  /** Sella una versión nueva a petición. Requiere `expedientes.indice.emit`. */
  async sealIndice(code: string): Promise<IndiceVersionResponse> {
    return apiClient.post<IndiceVersionResponse>(`${BASE_PATH}/${encodeURIComponent(code)}/indice/versiones`)
  },

  /** Veredicto sobre una versión sellada. "El expediente ha crecido" NO es una advertencia. */
  async verifyIndiceVersion(code: string, versionNumber: number): Promise<IndiceVerificacionResponse> {
    return apiClient.get<IndiceVerificacionResponse>(
      `${BASE_PATH}/${encodeURIComponent(code)}/indice/versiones/${versionNumber}/verificar`,
    )
  },

  /** URL de los BYTES CANÓNICOS sellados (no una reserialización del índice de hoy). */
  getIndiceXmlUrl(code: string, versionNumber: number): string {
    return apiClient.buildUrl(`${BASE_PATH}/${encodeURIComponent(code)}/indice/versiones/${versionNumber}/xml`)
  },

  /** URL de la representación imprimible. Se genera a demanda y no se guarda. */
  getIndicePdfUrl(code: string, versionNumber: number): string {
    return apiClient.buildUrl(`${BASE_PATH}/${encodeURIComponent(code)}/indice/versiones/${versionNumber}/pdf`)
  },
}
